import type { Game, Tile } from "..";
import type { Drop, itemId, rangeLabel } from "../../data/types";
import { System } from "../../lib/core";

export type SpawnEvents = {};

export type SpawnAssignment = {
  spawn: { itemId: itemId; tile: Tile }[];
  requested: number;
  fits: boolean;
};

export type SpawnAssignmentRequest = {
  from: Tile;
  drops: Drop | Record<string, number>;
  range: rangeLabel | rangeLabel[] | null;
  freed?: readonly Tile[];
  reserved?: readonly Tile[];
};

export class SpawnSystem extends System<Game>()<SpawnEvents>([]) {
  /**
   * Assign item drops to legal spawn tiles.
   *
   * Resource drops are ignored here; this system only places item instances.
   * `freed` tiles count as available for pending merges, while `reserved`
   * tiles are treated as occupied within the current spawn batch.
   */
  assign(opts: SpawnAssignmentRequest): SpawnAssignment {
    const dropEntries = Object.entries(opts.drops).filter(
      ([id]) => this.game.data.items[id as itemId],
    );
    const requested = dropEntries.reduce((sum, [, count]) => sum + count, 0);
    if (requested === 0) return { spawn: [], requested, fits: true };

    const tiles = this._findTiles({
      from: opts.from,
      count: requested,
      range: opts.range,
      freed: opts.freed,
      reserved: opts.reserved,
    });
    const spawn: SpawnAssignment["spawn"] = [];
    let i = 0;
    for (const [id, count] of dropEntries) {
      for (let n = 0; n < count; n++) {
        const tile = tiles[i++];
        if (!tile) return { spawn, requested, fits: false };
        spawn.push({ itemId: id as itemId, tile });
      }
    }
    return { spawn, requested, fits: true };
  }

  fits(opts: SpawnAssignmentRequest): boolean {
    return this.assign(opts).fits;
  }

  /**
   * Resolve pre-planned spawn destinations after earlier spawned items may have
   * occupied them. Keeps planned tiles when available and finds fallbacks when
   * needed, without assigning two spawned items to the same tile.
   */
  resolvePlanned(planned: { itemId: itemId; tile: Tile }[]): { itemId: itemId; tile: Tile }[] {
    const resolved: { itemId: itemId; tile: Tile }[] = [];
    const reserved: Tile[] = [];
    for (const { itemId, tile } of planned) {
      const isReserved = reserved.some((t) => t.key === tile.key);
      const spawnTile = tile.item || isReserved
        ? this._findTiles({ from: tile, count: 1, range: null, reserved })[0] ?? null
        : tile;
      if (!spawnTile) continue;
      reserved.push(spawnTile);
      resolved.push({ itemId, tile: spawnTile });
    }
    return resolved;
  }

  _findTiles(opts: {
    from: Tile;
    count: number;
    range: rangeLabel | rangeLabel[] | null;
    freed?: readonly Tile[];
    reserved?: readonly Tile[];
  }): Tile[] {
    const freedSet = opts.freed ? new Set(opts.freed.map((t) => t.key)) : null;
    const reservedSet = opts.reserved ? new Set(opts.reserved.map((t) => t.key)) : null;
    const { tiles } = this.game.grid.scan({
      mode: "nearest",
      from: opts.from,
      valid: (tile) => {
        if (tile.shade) return false;
        if (reservedSet?.has(tile.key)) return false;
        if (tile.item && !freedSet?.has(tile.key)) return false;
        return true;
      },
      count: opts.count,
      range: opts.range,
    });
    return tiles;
  }
}
